import {
  CommodityAttributeDto,
  CommodityCatalogueDto,
  CommodityDto,
  CreateCommodityWithCodeDto,
  UpdateCommodityCatalogueCommand,
} from '@billing/app/api'
import { CatalogueStore } from '@billing/stores/CatalogueStore'
import { CommodityStore } from '@billing/stores/CommodityStore'
import { BaseDrawerStore } from '@billing/stores/base/BaseDrawerStore'
import { action, computed, makeObservable, observable } from 'mobx'
import Papa from 'papaparse'
import { CatalogueFormProfile } from '../models/catalog.model'

type ParsedCatalogue = {
  name: string
  code: string
  packageIds: string
  usedByOrder: string
  isActive: string
  isSelectable: string
  description: string
  attributes: string
  parentCommodityCode: string
}

export class CatalogueDialogUIStore extends BaseDrawerStore<CommodityCatalogueDto> {
  selectedCatalogue?: CommodityCatalogueDto | undefined
  isCreateOpen = false
  isDeleteOpen = false
  isImportOpen = false
  file?: File

  constructor(
    private readonly catalogueStore: CatalogueStore,
    private readonly commodityStore: CommodityStore,
  ) {
    super(catalogueStore)
    makeObservable(this, {
      isCreateOpen: observable,
      isDeleteOpen: observable,
      isImportOpen: observable,
      file: observable,
      setFile: action,
      selectedCatalogue: observable,
      catalogueQuantityMap: computed,
      toggleCreate: action,
    })
  }

  public get catalogueQuantityMap(): Map<number, number> {
    return this.commodityStore.items.reduce<Map<number, number>>((map, commodity) => {
      function countCommodities(commodity: CommodityDto) {
        const catalogueId = commodity.catalogueId
        if (catalogueId) {
          map.set(catalogueId, (map.get(catalogueId) || 0) + 1)
        }
        if (Array.isArray(commodity.childCommodities)) {
          commodity.childCommodities.forEach(countCommodities)
        }
      }
      countCommodities(commodity)
      return map
    }, new Map<number, number>())
  }

  public toggleCreate(catalogue?: CommodityCatalogueDto): void {
    this.selectedCatalogue = catalogue
    this.isCreateOpen = !this.isCreateOpen
  }

  public toggleDelete(catalogue?: CommodityCatalogueDto): void {
    this.selectedCatalogue = catalogue
    this.isDeleteOpen = !this.isDeleteOpen
  }

  public async onSubmit(data: CatalogueFormProfile): Promise<void> {
    if (!this.selectedCatalogue?.id) {
      await this.catalogueStore.createCatalogue(data)
    } else {
      await this.catalogueStore.updateCatalogue(
        this.selectedCatalogue.id,
        data as UpdateCommodityCatalogueCommand,
      )
    }
  }

  public async delete(): Promise<void> {
    if (this.selectedCatalogue?.id) {
      await this.catalogueStore.deleteCatalogue(this.selectedCatalogue.id)
    }
  }

  public toggleImport(): void {
    this.isImportOpen = !this.isImportOpen
    if (this.isImportOpen) {
      this.file = undefined
    }
  }

  public setFile(file: File | undefined): void {
    this.file = file
  }

  private parseData = async (upload: File) => {
    return await new Promise((resolve, reject) => {
      try {
        Papa.parse(upload, {
          dynamicTyping: false,
          header: true,
          skipEmptyLines: true,
          encoding: 'windows-1252',
          complete: r => {
            resolve(r.data)
          },
        })
      } catch (e) {
        reject(e)
      }
    })
  }

  public async submitFile(): Promise<void> {
    const catalogueId = this.catalogueStore.selectedCatalogueId
    if (this.file) {
      const data = (await this.parseData(this.file)) as ParsedCatalogue[]
      const dataToSend = data.map(item => {
        const { attributes, isActive, isSelectable, ...data } = item
        return {
          ...data,
          catalogueId,
          attributes: attributes ? JSON.parse(attributes) : ([] as Array<CommodityAttributeDto>),
          packageIds: [],
          isActive: Boolean(isActive),
          isSelectable: Boolean(isSelectable),
        }
      }) as CreateCommodityWithCodeDto[]

      await this.commodityStore.createCommoditiesFromCsv(dataToSend)
      this.toggleImport()
      this.setFile(undefined)
    }
  }
}
